interface UTMParams {
  utm: string
  [key: string]: string | null | undefined
}

interface TrackedEntry {
  utm: string
  timestamp: string
  [key: string]: string | null | undefined
}

type TrackedUTMs = Record<string, TrackedEntry>

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1] ?? '') : null
}

function removeCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

export class UTMTracker {
  private cookieName: string
  private cookieExpireDays: number

  constructor(cookieName = 'utm_tracking') {
    this.cookieName = cookieName
    this.cookieExpireDays = 1
  }

  getTrackedUTMs(): TrackedUTMs {
    const raw = getCookie(this.cookieName)
    if (!raw)
      return {}

    try {
      return JSON.parse(raw) as TrackedUTMs
    }
    catch {
      return {}
    }
  }

  isTrackedToday(utmParams: UTMParams): boolean {
    const tracked = this.getTrackedUTMs()
    const utmKey = this.generateUTMKey(utmParams)

    if (!tracked[utmKey])
      return false

    const trackedDate = new Date(tracked[utmKey].timestamp)
    const today = new Date()

    return (
      trackedDate.getDate() === today.getDate()
      && trackedDate.getMonth() === today.getMonth()
      && trackedDate.getFullYear() === today.getFullYear()
    )
  }

  generateUTMKey(utmParams: UTMParams): string {
    return `rintisan_cache_${utmParams.utm}`
  }

  async trackUTM(
    utmParams: UTMParams,
    callback?: (params: UTMParams) => Promise<void>,
  ): Promise<{ tracked: boolean, reason?: string, data?: TrackedEntry }> {
    const cleanParams = Object.fromEntries(
      Object.entries(utmParams).filter(([_, v]) => v != null),
    ) as UTMParams

    if (Object.keys(cleanParams).length === 0) {
      return { tracked: false, reason: 'no_utm_params' }
    }

    if (!this.isTrackedToday(cleanParams)) {
      const tracked = this.getTrackedUTMs()
      const utmKey = this.generateUTMKey(cleanParams)

      tracked[utmKey] = {
        ...cleanParams,
        timestamp: new Date().toISOString(),
      }

      setCookie(this.cookieName, JSON.stringify(tracked), this.cookieExpireDays)

      if (callback && typeof callback === 'function') {
        try {
          await callback(cleanParams)
        }
        catch {}
      }

      return { tracked: true, data: tracked[utmKey] }
    }

    const utmKey = this.generateUTMKey(cleanParams)
    return { tracked: true, data: this.getTrackedUTMs()[utmKey] }
  }

  clearAllTracking(): void {
    removeCookie(this.cookieName)
  }

  cleanupOldEntries(): void {
    const tracked = this.getTrackedUTMs()
    const now = new Date()
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)

    const cleaned = Object.entries(tracked).reduce<TrackedUTMs>((acc, [key, value]) => {
      const entryDate = new Date(value.timestamp)
      if (entryDate > oneDayAgo) {
        acc[key] = value
      }
      return acc
    }, {})

    if (Object.keys(cleaned).length === 0) {
      this.clearAllTracking()
    }
    else {
      setCookie(this.cookieName, JSON.stringify(cleaned), this.cookieExpireDays)
    }
  }

  getAllTrackedData(): TrackedUTMs {
    return this.getTrackedUTMs()
  }
}
